/* iter-121.53 — Trial days countdown chip rendered in the TopBar.
 *
 * Bug fix: trial users had no visible signal in the app header for
 * how many days remained in their trial. This chip:
 *   • Only renders when the tenant lifecycle is "trial" and
 *     `trial_ends_at` is present on /auth/me (so paid + suspended
 *     users see nothing — no clutter).
 *   • Shows "N days left" with progressive styling: amber when ≤ 7,
 *     red when ≤ 3, neutral when more time remains.
 *   • Tooltip carries the exact ISO end date so users can verify.
 *   • Click → opens the Billing page (existing route) so users
 *     have a one-tap path to upgrade.
 *
 * All values come from `user` (auth context). No new API surface;
 * `auth_routes.py::_user_public` was extended to surface
 * `tenant_lifecycle` + `tenant_trial_ends_at`.
 */
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Clock } from "lucide-react";
import { useAuth } from "@/lib/auth-context";

export default function TrialDaysChip() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const info = useMemo(() => {
        if (!user) return null;
        if (user.tenant_lifecycle !== "trial") return null;
        const iso = user.tenant_trial_ends_at;
        if (!iso) return null;
        const end = new Date(iso);
        if (isNaN(end.getTime())) return null;
        const ms = end.getTime() - Date.now();
        // Round UP — "0 days" feels worse than "1 day" while there's
        // still some hours left. Negative = trial expired.
        const days = Math.ceil(ms / 86_400_000);
        return { days, end };
    }, [user]);

    if (!info) return null;

    const { days, end } = info;
    const expired = days <= 0;
    const tone =
        expired ? "bg-destructive/15 text-destructive border-destructive/30" :
        days <= 3 ? "bg-destructive/10 text-destructive border-destructive/30" :
        days <= 7 ? "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30" :
                    "bg-secondary text-foreground border-border";

    const label = expired
        ? "Trial expired"
        : (days === 1 ? "1 day left" : `${days} days left`);

    return (
        <button
            type="button"
            onClick={() => navigate("/billing")}
            className={`h-9 px-2.5 flex items-center gap-1.5 border rounded-sm text-[11px] font-mono uppercase tracking-wider hover:opacity-90 transition-opacity ${tone}`}
            title={`Free trial ends ${end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · click to upgrade`}
            data-testid="trial-days-chip"
            aria-label={label}
        >
            <Clock className="h-3.5 w-3.5" />
            <span>{label}</span>
        </button>
    );
}
